import { range } from "lodash-es";
import type React from "react";
import type { ChangeEvent, Ref } from "react";
import { useRef, useState } from "react";

import type { VideoMediaProps } from "@/components/Media/VideoMedia";
import { VideoMedia } from "@/components/Media/VideoMedia";
import { FileInput } from "@/design-system/FileInput";
import { Inline } from "@/design-system/Inline";
import { isFileTooBig, MAXIMUM_IMAGE_FILE_SIZE_IN_MEGA_BYTES } from "@/helpers/file-size";
import { ALLOWED_FILE_TYPES } from "@/helpers/file-types";
import { mergeRefs } from "@/helpers/mergeRefs";
import { isDefined } from "@/helpers/util";
import { useUploadVideo } from "@/hooks/Network/useUploadVideo";

import type { FormVideo } from "./useVideoInput";
import { isVideoUploaded, useVideoInput } from "./useVideoInput";

export interface VideoInputProps extends Pick<React.InputHTMLAttributes<HTMLInputElement>, "id" | "aria-invalid"> {
  ref?: Ref<HTMLInputElement>;
  value: FormVideo[];
  onChange: (files: FormVideo[]) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  texts?: VideoMediaProps["texts"];
  nOfVideos?: number;
  disabled?: boolean;
  shouldSkipProcessing?: boolean;
}

export function VideoInput({
  value: videos,
  onChange,
  texts,
  nOfVideos = 1,
  shouldSkipProcessing,
  ref,
  ...props
}: VideoInputProps): React.ReactNode {
  const [isDragging, setIsDragging] = useState(false);

  const innerRef = useRef<HTMLInputElement>(null);
  const replacementIndex = useRef<number | undefined>(undefined);

  const { uploadFormVideo } = useUploadVideo({
    onProgress: ({ name, progress }) => {
      onChange(
        [...videos].map((video) => {
          if (!isVideoUploaded(video) && video.file.name === name) {
            return { ...video, uploadProgress: progress };
          }

          return video;
        }),
      );
    },
    shouldProcess: !shouldSkipProcessing,
  });
  const { addVideos, removeVideo, replaceVideo } = useVideoInput({
    selectedVideos: videos,
    maximumFiles: nOfVideos,
    onChange,
    uploadFn: uploadFormVideo,
  });

  const onOpenFileSelector = () => {
    if (!innerRef.current) return;

    innerRef.current.click();
  };

  const onRemoveFile = (idx: number) => {
    if (innerRef.current) {
      removeVideo(videos[idx]);
      innerRef.current.value = "";
      props.onBlur?.();
    }
  };

  const onEditFile = (idx: number) => {
    replacementIndex.current = idx;

    onOpenFileSelector();
  };

  const onChangeFile = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files === null) {
      return;
    }

    if (isDefined(replacementIndex.current)) {
      replaceVideo(videos[replacementIndex.current], event.target.files[0]);
      replacementIndex.current = undefined;
    } else {
      addVideos(event.target.files);
    }
  };

  return (
    <Inline
      gap="sm"
      onDrop={(event) => {
        if (event.dataTransfer.files && innerRef.current) {
          innerRef.current.files = event.dataTransfer.files;
          addVideos(event.dataTransfer.files);
          setIsDragging(false);
          props.onBlur?.();

          event.preventDefault();
        }
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
      }}
      onDragOverCapture={() => setIsDragging(true)}
      onDragLeaveCapture={() => setIsDragging(false)}
    >
      {[...range(nOfVideos)].map((idx) => {
        const isHidden = videos?.length < idx;

        if (isHidden) return;

        return (
          <VideoMedia
            video={videos[idx]}
            data-testid="video-media"
            key="video-media-fragment"
            aria-invalid={
              props["aria-invalid"] || (videos[idx] && isFileTooBig(videos[idx], MAXIMUM_IMAGE_FILE_SIZE_IN_MEGA_BYTES))
            }
            onAdd={() => onOpenFileSelector()}
            onDelete={() => onRemoveFile(idx)}
            onEdit={() => onEditFile(idx)}
            onFocus={props.onFocus}
            onBlur={props.onBlur}
            texts={texts}
            {...props}
            dropActive={isDragging}
            disabled={props.disabled}
          />
        );
      })}

      <FileInput
        data-testid="video-input"
        tabIndex={-1}
        {...props}
        accept={ALLOWED_FILE_TYPES.VIDEO}
        ref={mergeRefs(innerRef, ref)}
        onChange={onChangeFile}
        disabled={props.disabled}
      />
    </Inline>
  );
}
